/**
 * Profile archive: load facets + filterable grid via REST (no full page reload).
 */

type ProfileCard = {
  id: number;
  title: string;
  permalink: string;
  image: string;
  height: string;
  width: string;
  pdf_url: string;
  cad_url: string;
  categories: { name: string; url: string }[];
};

/** Heights/widths use `value` + `label`; wood types use `slug` + `name` (see REST facets). */
type FacetRowPayload = {
  value?: string;
  label?: string;
  slug?: string;
  name?: string;
  count?: number;
};

type FacetPayload = {
  categories: { slug: string; name: string }[];
  woodTypes?: FacetRowPayload[];
  heights: FacetRowPayload[];
  widths: FacetRowPayload[];
};

type ProfilesPayload = {
  items: ProfileCard[];
};

type Localized = {
  restProfiles: string;
  restFacets: string;
  profileScope?: string;
  lockedCategory?: string;
  lockedWoodType?: string;
  strings: {
    loading: string;
    resultsLoaded: string;
    noResults: string;
    loadError: string;
    pdfDrawing: string;
    cadDrawing: string;
    imageSoon: string;
    selectLabel: string;
    categoryAll: string;
    facetSearch?: string;
  };
};

type FacetRow = { value: string; label: string; count: number };

type FacetMultiController = {
  populate(rows: FacetRow[]): void;
  getValues(): string[];
  clear(): void;
  close(): void;
};

type FacetCategoryController = {
  populate(rows: FacetRow[]): void;
  clear(): void;
  close(): void;
};

const PROFILE_ARCHIVE_CATEGORY_RADIO_NAME = 'profile-archive-category-pick';

function getLocalized(): Localized | null {
  const w = window as unknown as { shawStewartProfileArchive?: Localized };
  return w.shawStewartProfileArchive ?? null;
}

function escapeHtml(text: string): string {
  return String(text).replace(/[&<>"']/g, (ch) => {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return map[ch] ?? ch;
  });
}

function normalizeProfileScope(raw: unknown): 'stock' | 'custom' {
  return raw === 'custom' ? 'custom' : 'stock';
}

function restUrlWithScopeAndParams(
  base: string,
  scope: 'stock' | 'custom',
  params: Record<string, string>
): string {
  try {
    const u = new URL(base, window.location.origin);
    u.searchParams.set('scope', scope);
    for (const [k, v] of Object.entries(params)) {
      if (v.trim() !== '') {
        u.searchParams.set(k, v);
      }
    }
    return u.pathname + u.search + u.hash;
  } catch {
    const joiner = base.includes('?') ? '&' : '?';
    const query = new URLSearchParams({ scope, ...params }).toString();
    return `${base}${joiner}${query}`;
  }
}

function debounce(fn: () => void, ms: number): () => void {
  let t: ReturnType<typeof setTimeout> | undefined;
  return () => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(), ms);
  };
}

type FacetRowSortOrder = 'label' | 'numeric';

function parseFacetDimensionInches(value: string): number {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function sortFacetRowsForDisplay(
  rows: FacetRow[],
  isSelected: (value: string) => boolean,
  orderBy: FacetRowSortOrder
): FacetRow[] {
  return [...rows].sort((a, b) => {
    const aSel = isSelected(a.value);
    const bSel = isSelected(b.value);
    if (aSel !== bSel) {
      return aSel ? -1 : 1;
    }
    if (orderBy === 'numeric') {
      return parseFacetDimensionInches(a.value) - parseFacetDimensionInches(b.value);
    }
    return a.label.localeCompare(b.label);
  });
}

function attachFacetMulti(
  root: HTMLElement,
  selectLabel: string,
  debouncedReload: () => void,
  closeAllPeerPanels: () => void,
  rowSortOrder: FacetRowSortOrder = 'label'
): FacetMultiController {
  const trigger = root.querySelector<HTMLButtonElement>('[data-facet-ms-trigger]');
  const panel = root.querySelector<HTMLElement>('[data-facet-ms-panel]');
  const search = root.querySelector<HTMLInputElement>('[data-facet-ms-search]');
  const list = root.querySelector<HTMLUListElement>('[data-facet-ms-list]');
  const triggerText = root.querySelector<HTMLElement>('[data-facet-ms-trigger-text]');
  if (!trigger || !panel || !search || !list || !triggerText) {
    return {
      populate: () => {},
      getValues: () => [],
      clear: () => {},
      close: () => {},
    };
  }

  let rowsCache: FacetRow[] = [];

  const updateScrollIndicators = (): void => {
    const canScroll = list.scrollHeight > list.clientHeight + 1;
    list.classList.toggle('profile-archive__facet-ms-list--can-scroll', canScroll);
    if (!canScroll) {
      list.classList.remove('profile-archive__facet-ms-list--not-top', 'profile-archive__facet-ms-list--not-bottom');
      return;
    }

    const atTop = list.scrollTop <= 1;
    const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
    list.classList.toggle('profile-archive__facet-ms-list--not-top', !atTop);
    list.classList.toggle('profile-archive__facet-ms-list--not-bottom', !atBottom);
  };

  const getValues = (): string[] =>
    Array.from(list.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked'))
      .map((el) => el.value)
      .filter((v) => v !== '');

  const updateTriggerLabel = (): void => {
    const vals = getValues();
    if (vals.length === 0) {
      triggerText.textContent = selectLabel;
      return;
    }
    const labels = vals.map((v) => rowsCache.find((r) => r.value === v)?.label ?? v);
    const joined = labels.join(', ');
    const max = 44;
    triggerText.textContent = joined.length > max ? `${joined.slice(0, max)}…` : joined;
  };

  const filterList = (): void => {
    const q = search.value.trim().toLowerCase();
    list.querySelectorAll<HTMLLIElement>('.profile-archive__facet-ms-item').forEach((li) => {
      const hay = (li.dataset.filterText ?? '').toLowerCase();
      li.hidden = q !== '' && !hay.includes(q);
    });
    updateScrollIndicators();
  };

  const close = (): void => {
    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    root.classList.remove('profile-archive__facet-ms--open');
  };

  const open = (): void => {
    closeAllPeerPanels();
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    root.classList.add('profile-archive__facet-ms--open');
    search.value = '';
    filterList();
    search.focus();
    requestAnimationFrame(() => updateScrollIndicators());
  };

  trigger.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!panel.hidden) {
      close();
      return;
    }
    open();
  });

  document.addEventListener('mousedown', (e) => {
    if (!root.contains(e.target as Node)) {
      close();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || panel.hidden) {
      return;
    }
    if (root.contains(document.activeElement)) {
      close();
      trigger.focus();
    }
  });

  search.addEventListener('input', () => filterList());
  list.addEventListener('scroll', () => updateScrollIndicators(), { passive: true });

  list.addEventListener('change', () => {
    updateTriggerLabel();
    debouncedReload();
  });

  const populate = (rows: FacetRow[]): void => {
    const selectedBefore = new Set(getValues());
    const sorted = sortFacetRowsForDisplay(rows, (v) => selectedBefore.has(v), rowSortOrder);
    rowsCache = sorted;
    list.innerHTML = sorted
      .map((r) => {
        const filterText = `${r.label} ${r.value} ${r.count}`.toLowerCase();
        const checkedAttr = selectedBefore.has(r.value) ? ' checked' : '';
        return `<li class="profile-archive__facet-ms-item" data-filter-text="${escapeHtml(filterText)}">
          <label class="profile-archive__facet-ms-row">
            <input type="checkbox" class="profile-archive__facet-ms-check" value="${escapeHtml(r.value)}"${checkedAttr} />
            <span class="profile-archive__facet-ms-text">${escapeHtml(r.label)} <span class="profile-archive__facet-ms-count">(${escapeHtml(String(r.count))})</span></span>
          </label>
        </li>`;
      })
      .join('');
    filterList();
    updateTriggerLabel();
    requestAnimationFrame(() => updateScrollIndicators());
  };

  const clear = (): void => {
    list.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((c) => {
      c.checked = false;
    });
    updateTriggerLabel();
  };

  return {
    populate,
    getValues,
    clear,
    close,
  };
}

function attachFacetCategory(
  root: HTMLElement,
  syncSelect: HTMLSelectElement,
  selectLabel: string,
  allLabel: string,
  debouncedReload: () => void,
  closeAllPeerPanels: () => void
): FacetCategoryController {
  const trigger = root.querySelector<HTMLButtonElement>('[data-facet-ms-trigger]');
  const panel = root.querySelector<HTMLElement>('[data-facet-ms-panel]');
  const search = root.querySelector<HTMLInputElement>('[data-facet-ms-search]');
  const list = root.querySelector<HTMLUListElement>('[data-facet-ms-list]');
  const triggerText = root.querySelector<HTMLElement>('[data-facet-ms-trigger-text]');
  if (!trigger || !panel || !search || !list || !triggerText) {
    return {
      populate: () => {},
      clear: () => {},
      close: () => {},
    };
  }

  let rowsCache: FacetRow[] = [];

  const updateScrollIndicators = (): void => {
    const canScroll = list.scrollHeight > list.clientHeight + 1;
    list.classList.toggle('profile-archive__facet-ms-list--can-scroll', canScroll);
    if (!canScroll) {
      list.classList.remove('profile-archive__facet-ms-list--not-top', 'profile-archive__facet-ms-list--not-bottom');
      return;
    }
    const atTop = list.scrollTop <= 1;
    const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
    list.classList.toggle('profile-archive__facet-ms-list--not-top', !atTop);
    list.classList.toggle('profile-archive__facet-ms-list--not-bottom', !atBottom);
  };

  const syncSelectFromRadios = (): void => {
    const checked = list.querySelector<HTMLInputElement>('input[type="radio"]:checked');
    syncSelect.value = checked?.value ?? '';
  };

  const updateTriggerLabel = (): void => {
    const checked = list.querySelector<HTMLInputElement>('input[type="radio"]:checked');
    const val = checked?.value ?? '';
    if (val === '') {
      triggerText.textContent = selectLabel;
      return;
    }
    triggerText.textContent = rowsCache.find((r) => r.value === val)?.label ?? val;
  };

  const filterList = (): void => {
    const q = search.value.trim().toLowerCase();
    list.querySelectorAll<HTMLLIElement>('.profile-archive__facet-ms-item').forEach((li) => {
      const hay = (li.dataset.filterText ?? '').toLowerCase();
      li.hidden = q !== '' && !hay.includes(q);
    });
    updateScrollIndicators();
  };

  const close = (): void => {
    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    root.classList.remove('profile-archive__facet-ms--open');
  };

  const open = (): void => {
    closeAllPeerPanels();
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    root.classList.add('profile-archive__facet-ms--open');
    search.value = '';
    filterList();
    search.focus();
    requestAnimationFrame(() => updateScrollIndicators());
  };

  trigger.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!panel.hidden) {
      close();
      return;
    }
    open();
  });

  document.addEventListener('mousedown', (e) => {
    if (!root.contains(e.target as Node)) {
      close();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || panel.hidden) {
      return;
    }
    if (root.contains(document.activeElement)) {
      close();
      trigger.focus();
    }
  });

  search.addEventListener('input', () => filterList());
  list.addEventListener('scroll', () => updateScrollIndicators(), { passive: true });

  list.addEventListener('change', () => {
    syncSelectFromRadios();
    updateTriggerLabel();
    close();
    debouncedReload();
  });

  const populate = (rows: FacetRow[]): void => {
    const selectedBefore = syncSelect.value;
    const sorted = [...rows].sort((a, b) => {
      const aSel = selectedBefore !== '' && a.value === selectedBefore;
      const bSel = selectedBefore !== '' && b.value === selectedBefore;
      if (aSel !== bSel) return aSel ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
    rowsCache = sorted;

    syncSelect.innerHTML = `<option value="">${escapeHtml(allLabel)}</option>`;
    for (const r of sorted) {
      syncSelect.insertAdjacentHTML(
        'beforeend',
        `<option value="${escapeHtml(r.value)}">${escapeHtml(r.label)}</option>`
      );
    }
    // Restore previous selection if it still exists.
    if (selectedBefore !== '') {
      syncSelect.value = selectedBefore;
    }

    const clearFilterText = allLabel.toLowerCase();
    const isEmptySelected = selectedBefore === '' || selectedBefore === null;
    const chunks: string[] = [
      `<li class="profile-archive__facet-ms-item" data-filter-text="${escapeHtml(clearFilterText)}">
        <label class="profile-archive__facet-ms-row profile-archive__facet-ms-row--single">
          <input type="radio" name="${PROFILE_ARCHIVE_CATEGORY_RADIO_NAME}" value="" class="profile-archive__facet-ms-radio"${
            isEmptySelected ? ' checked' : ''
          } />
          <span class="profile-archive__facet-ms-text">${escapeHtml(allLabel)}</span>
        </label>
      </li>`,
    ];
    for (const r of sorted) {
      const filterText = `${r.label} ${r.value} ${r.count}`.toLowerCase();
      const countHtml =
        r.count > 0
          ? ` <span class="profile-archive__facet-ms-count">(${escapeHtml(String(r.count))})</span>`
          : '';
      const checkedAttr = selectedBefore === r.value ? ' checked' : '';
      chunks.push(
        `<li class="profile-archive__facet-ms-item" data-filter-text="${escapeHtml(filterText)}">
          <label class="profile-archive__facet-ms-row profile-archive__facet-ms-row--single">
            <input type="radio" name="${PROFILE_ARCHIVE_CATEGORY_RADIO_NAME}" value="${escapeHtml(
              r.value
            )}" class="profile-archive__facet-ms-radio"${checkedAttr} />
            <span class="profile-archive__facet-ms-text">${escapeHtml(r.label)}${countHtml}</span>
          </label>
        </li>`
      );
    }
    list.innerHTML = chunks.join('');
    filterList();
    syncSelectFromRadios();
    updateTriggerLabel();
    requestAnimationFrame(() => updateScrollIndicators());
  };

  const clear = (): void => {
    const emptyRadio = list.querySelector<HTMLInputElement>(
      `input[type="radio"][name="${PROFILE_ARCHIVE_CATEGORY_RADIO_NAME}"][value=""]`
    );
    if (emptyRadio) {
      emptyRadio.checked = true;
    }
    syncSelectFromRadios();
    updateTriggerLabel();
  };

  return {
    populate,
    clear,
    close,
  };
}

const iconVertical = `<svg class="profile-archive__card-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.75"><path d="M12 3v18M9 6h6M9 12h6M9 18h6"/></svg>`;
const iconHorizontal = `<svg class="profile-archive__card-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.75"><path d="M3 12h18M6 9v6M12 9v6M18 9v6"/></svg>`;
const iconPdf = `<svg class="profile-archive__card-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 15h8v2H8v-2zm0-4h8v2H8v-2z"/></svg>`;
const iconCad = `<svg class="profile-archive__card-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.75"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M10 12h4M10 16h4"/></svg>`;

function renderCategoryLinks(categories: ProfileCard['categories']): string {
  if (!categories.length) {
    return '—';
  }
  return categories
    .map(
      (c) =>
        `<a class="profile-archive__card-category-link" href="${escapeHtml(c.url)}">${escapeHtml(c.name)}</a>`
    )
    .join('<span class="profile-archive__card-category-sep">, </span>');
}

function renderCard(item: ProfileCard, labels: Localized['strings']): string {
  const img = item.image
    ? `<a class="profile-archive__card-media" href="${escapeHtml(item.permalink)}"><img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy" decoding="async" width="400" height="300"></a>`
    : `<div class="profile-archive__card-placeholder">${escapeHtml(labels.imageSoon)}</div>`;

  const pdfRow =
    item.pdf_url !== ''
      ? `<li class="profile-archive__card-meta-item">${iconPdf}<a href="${escapeHtml(item.pdf_url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(labels.pdfDrawing)}</a></li>`
      : '';
  const cadRow =
    item.cad_url !== ''
      ? `<li class="profile-archive__card-meta-item">${iconCad}<a href="${escapeHtml(item.cad_url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(labels.cadDrawing)}</a></li>`
      : '';

  return `
<article class="profile-archive__card">
  ${img}
  <h2 class="profile-archive__card-title"><a href="${escapeHtml(item.permalink)}">${escapeHtml(item.title)}</a></h2>
  <hr class="profile-archive__card-rule" />
  <ul class="profile-archive__card-meta">
    <li class="profile-archive__card-meta-item">${iconVertical}<span>${escapeHtml(item.height)}</span></li>
    <li class="profile-archive__card-meta-item">${iconHorizontal}<span>${escapeHtml(item.width)}</span></li>
    ${pdfRow}
    ${cadRow}
  </ul>
  <p class="profile-archive__card-category">${renderCategoryLinks(item.categories ?? [])}</p>
</article>`;
}

function mapFacetRows(rows: FacetRowPayload[]): FacetRow[] {
  return rows.map((r) => {
    const value = (r.value ?? r.slug ?? '').trim();
    const label = (r.label ?? r.name ?? value).trim();

    return {
      value,
      label: label !== '' ? label : value,
      count: typeof r.count === 'number' ? r.count : 0,
    };
  });
}

export default function initProfileArchive(): void {
  const root = document.querySelector('[data-profile-archive]');
  const grid = document.querySelector('[data-profile-archive-grid]');
  const status = document.getElementById('profile-archive-status');
  const form = document.getElementById('profile-archive-filters') as HTMLFormElement | null;
  const loc = getLocalized();

  if (!root || !grid || !form || !loc) {
    return;
  }

  const part = form.querySelector('#profile-archive-part') as HTMLInputElement;
  const categoryRoot = form.querySelector<HTMLElement>('[data-facet-category]');
  const category = form.querySelector('#profile-archive-category') as HTMLSelectElement | null;
  const resetBtn = document.getElementById('profile-archive-reset');
  const loadingEl = document.getElementById('profile-archive-loading');

  const woodRoot = form.querySelector<HTMLElement>('[data-facet-ms="wood"]');
  const heightRoot = form.querySelector<HTMLElement>('[data-facet-ms="height"]');
  const widthRoot = form.querySelector<HTMLElement>('[data-facet-ms="width"]');

  const lockedCategory = typeof loc.lockedCategory === 'string' ? loc.lockedCategory.trim() : '';
  const lockedWoodType = typeof loc.lockedWoodType === 'string' ? loc.lockedWoodType.trim() : '';
  const profileScope = normalizeProfileScope(loc.profileScope);
  const selectLabel = loc.strings.selectLabel;
  const categoryAllLabel = loc.strings.categoryAll;

  const facetRefs: {
    wood: FacetMultiController | null;
    height: FacetMultiController | null;
    width: FacetMultiController | null;
    category: FacetCategoryController | null;
  } = {
    wood: null,
    height: null,
    width: null,
    category: null,
  };

  const closeAllFacetPanels = (): void => {
    facetRefs.wood?.close();
    facetRefs.height?.close();
    facetRefs.width?.close();
    facetRefs.category?.close();
  };

  const setLoadingVisible = (visible: boolean): void => {
    if (!loadingEl) {
      return;
    }
    if (visible) {
      loadingEl.removeAttribute('hidden');
      loadingEl.setAttribute('aria-hidden', 'false');
    } else {
      loadingEl.setAttribute('hidden', '');
      loadingEl.setAttribute('aria-hidden', 'true');
    }
  };

  const getCategoryParam = (): string => {
    if (lockedCategory !== '') {
      return lockedCategory;
    }
    return category?.value ?? '';
  };

  const getWoodTypeParamString = (): string => {
    if (lockedWoodType !== '') {
      return lockedWoodType;
    }
    return facetRefs.wood ? facetRefs.wood.getValues().join(',') : '';
  };

  const getHeightParamString = (): string => (facetRefs.height ? facetRefs.height.getValues().join(',') : '');
  const getWidthParamString = (): string => (facetRefs.width ? facetRefs.width.getValues().join(',') : '');

  const setStatus = (msg: string) => {
    if (status) status.textContent = msg;
  };

  const fetchJson = async <T,>(url: string): Promise<T> => {
    const res = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    return (await res.json()) as T;
  };

  const loadProfiles = async () => {
    const u = new URL(loc.restProfiles, window.location.origin);
    u.searchParams.set('scope', profileScope);
    if (part.value.trim()) {
      u.searchParams.set('part', part.value.trim());
    }
    const cat = getCategoryParam();
    if (cat !== '') {
      u.searchParams.set('category', cat);
    }
    const heights = facetRefs.height?.getValues() ?? [];
    if (heights.length > 0) {
      u.searchParams.set('height', heights.join(','));
    }
    const widths = facetRefs.width?.getValues() ?? [];
    if (widths.length > 0) {
      u.searchParams.set('width', widths.join(','));
    }
    const wood = getWoodTypeParamString();
    if (wood !== '') {
      u.searchParams.set('wood_type', wood);
    }
    const url = u.pathname + u.search;

    grid.setAttribute('aria-busy', 'true');
    setLoadingVisible(true);
    setStatus(loc.strings.loading);

    try {
      const data = await fetchJson<ProfilesPayload>(url);
      if (!data.items || data.items.length === 0) {
        grid.innerHTML = `<p class="profile-archive__empty">${escapeHtml(loc.strings.noResults)}</p>`;
        setStatus(loc.strings.noResults);
      } else {
        grid.innerHTML = data.items.map((it) => renderCard(it, loc.strings)).join('');
        setStatus(loc.strings.resultsLoaded.replace('%d', String(data.items.length)));
      }
    } catch {
      grid.innerHTML = `<p class="profile-archive__empty">${escapeHtml(loc.strings.loadError)}</p>`;
      setStatus(loc.strings.loadError);
    } finally {
      grid.removeAttribute('aria-busy');
      setLoadingVisible(false);
    }
  };

  const debouncedProfiles = debounce(() => void loadProfiles(), 350);
  const debouncedFacets = debounce(() => void loadFacets(), 250);

  facetRefs.height =
    heightRoot !== null
      ? attachFacetMulti(
          heightRoot,
          selectLabel,
          () => {
            debouncedProfiles();
            debouncedFacets();
          },
          closeAllFacetPanels,
          'numeric'
        )
      : null;
  facetRefs.width =
    widthRoot !== null
      ? attachFacetMulti(
          widthRoot,
          selectLabel,
          () => {
            debouncedProfiles();
            debouncedFacets();
          },
          closeAllFacetPanels,
          'numeric'
        )
      : null;
  facetRefs.wood =
    woodRoot !== null
      ? attachFacetMulti(woodRoot, selectLabel, () => {
          debouncedProfiles();
          debouncedFacets();
        }, closeAllFacetPanels)
      : null;
  facetRefs.category =
    categoryRoot !== null && category !== null
      ? attachFacetCategory(
          categoryRoot,
          category,
          selectLabel,
          categoryAllLabel,
          () => {
            debouncedProfiles();
            debouncedFacets();
          },
          closeAllFacetPanels
        )
      : null;

  const loadFacets = async () => {
    const data = await fetchJson<FacetPayload>(
      restUrlWithScopeAndParams(loc.restFacets, profileScope, {
        part: part.value.trim(),
        category: getCategoryParam(),
        height: getHeightParamString(),
        width: getWidthParamString(),
        wood_type: getWoodTypeParamString(),
      })
    );
    facetRefs.category?.populate(mapFacetRows(data.categories));
    facetRefs.height?.populate(mapFacetRows(data.heights));
    facetRefs.width?.populate(mapFacetRows(data.widths));
    if (facetRefs.wood) {
      const woods = Array.isArray(data.woodTypes) ? data.woodTypes : [];
      facetRefs.wood.populate(mapFacetRows(woods));
    }
  };

  part.addEventListener('input', () => {
    debouncedProfiles();
    void loadFacets();
  });

  resetBtn?.addEventListener('click', () => {
    part.value = '';
    facetRefs.category?.clear();
    if (facetRefs.wood && lockedWoodType === '') facetRefs.wood.clear();
    facetRefs.height?.clear();
    facetRefs.width?.clear();
    void loadFacets();
    void loadProfiles();
  });

  void (async () => {
    try {
      await loadFacets();
    } catch {
      setStatus(loc.strings.loadError);
    }
    await loadProfiles();
  })();
}
